import { APP_SCHEME } from '../config/appVariant';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { Session, User } from '@supabase/supabase-js';
import * as WebBrowser from 'expo-web-browser';
import { supabase } from '../lib/supabase';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;
  signUp: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  signIn: async () => ({ user: null, session: null }),
  signUp: async () => ({ user: null, session: null }),
  signInWithGoogle: async () => {},
  signInWithApple: async () => {},
  signOut: async () => {},
  resetPassword: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    console.log('[AuthContext] Setting up auth listeners...');
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      console.log('[AuthContext] Initial session:', initialSession ? 'exists' : 'none');
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
    }).catch(() => {
      console.error('[AuthContext] Error getting initial session');
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      console.log('[AuthContext] Auth state changed:', _event, newSession ? 'exists' : 'none');
      setSession(newSession);
      setUser(newSession?.user ?? null);
    });

    // Cleanup function
    return () => {
      console.log('[AuthContext] Unsubscribing auth listener');
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    console.log('Attempting sign in...');
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      console.log('Sign in successful');
      return { user: data.user, session: data.session };
    } catch (error) {
      console.error('Sign in error');
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    console.log('Attempting sign up...');
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
        },
      });
      if (error) throw error;
      console.log('Sign up response received');
      
      if (data.user?.identities?.length === 0) {
        throw new Error('This email is already registered. Please sign in instead.');
      }

      // Check if email confirmation is required
      if (data.session === null && data.user?.confirmation_sent_at) {
        throw new Error('Please check your email for the confirmation link.');
      }

      return { user: data.user, session: data.session };
    } catch (error) {
      console.error('Sign up error');
      throw error;
    }
  };

  const signOut = async () => {
    console.log('Attempting sign out...');
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // If session is missing, just clear local state
        if (error.message?.includes('Auth session missing')) {
          console.log('Session already cleared, updating local state');
          setSession(null);
          setUser(null);
          return;
        }
        throw error;
      }
      console.log('Sign out successful');
    } catch {
      console.error('Sign out error');
      // Clear local state even if sign out fails
      setSession(null);
      setUser(null);
    }
  };

  const signInWithOAuth = async (provider: 'google' | 'apple') => {
    const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
    console.log(`[${providerName} OAuth] Starting sign in...`);
    console.log(`[${providerName} OAuth] Platform:`, Platform.OS);
    
    try {
      if (Platform.OS === 'web') {
        // Web platform - use standard OAuth flow
        const redirectTo = typeof window !== 'undefined'
          ? `${window.location.origin}/auth/callback` 
          : undefined;
        
        console.log(`[${providerName} OAuth] Redirect URL:`, redirectTo);
        
        const { error } = await supabase.auth.signInWithOAuth({
          provider,
          options: { redirectTo },
        });
        
        if (error) throw error;
      } else {
        // Native platform - manually open browser
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            skipBrowserRedirect: true,
            redirectTo: `${APP_SCHEME}://`,
          },
        });
        
        if (error) {
          console.error(`[${providerName} OAuth] Error`);
          throw error;
        }
        
        console.log(`[${providerName} OAuth] Opening browser`);
        
        // Open the OAuth URL in the system browser
        const result = await WebBrowser.openAuthSessionAsync(
          data.url,
          `${APP_SCHEME}://`
        );
        
        console.log(`[${providerName} OAuth] Browser result:`, result.type);
        
        if (result.type === 'success' && result.url) {
          console.log(`[${providerName} OAuth] Callback received`);
          
          // Extract the session from the callback URL
          const url = new URL(result.url);
          
          // Try hash fragment first (standard OAuth)
          let params = new URLSearchParams(url.hash.substring(1));
          let accessToken = params.get('access_token');
          let refreshToken = params.get('refresh_token');
          
          // If not in hash, try query parameters
          if (!accessToken) {
            params = new URLSearchParams(url.search);
            accessToken = params.get('access_token');
            refreshToken = params.get('refresh_token');
          }
          
          console.log(`[${providerName} OAuth] Access token found:`, !!accessToken);
          console.log(`[${providerName} OAuth] Refresh token found:`, !!refreshToken);
          
          if (accessToken && refreshToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (sessionError) {
              console.error(`[${providerName} OAuth] Session error`);
              throw sessionError;
            }
            console.log(`[${providerName} OAuth] Session set successfully`);
          } else {
            console.error(`[${providerName} OAuth] No tokens found in callback URL`);
            throw new Error('Authentication failed: No tokens found in callback URL. Please try again.');
          }
        } else if (result.type === 'cancel') {
          console.log(`[${providerName} OAuth] User cancelled`);
        } else {
          console.log(`[${providerName} OAuth] Unexpected result type:`, result.type);
        }
      }
      
      console.log(`[${providerName} OAuth] Sign in initiated successfully`);
    } catch (error) {
      console.error(`[${providerName} OAuth] Exception`);
      throw error;
    }
  };

  const signInWithGoogle = () => signInWithOAuth('google');
  const signInWithApple = () => signInWithOAuth('apple');

  const resetPassword = async (email: string) => {
    console.log('Attempting password reset...');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
      });
      if (error) throw error;
      console.log('Password reset email sent');
    } catch (error) {
      console.error('Password reset error');
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        signIn,
        signUp,
        signInWithGoogle,
        signInWithApple,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
